import { createPortal } from "react-dom";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Check, Plus, RefreshCw, Sparkles, Instagram, Save, BadgeCheck } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { Input } from "../ui/input";

export const IG_POST = /^https:\/\/(www\.)?instagram\.com\/(p|reel|tv)\/[\w-]+\/?(\?.*)?$/;

const IgInput = ({ id, value, onChange }) => {
  const bad = value && !IG_POST.test(value.trim());
  return (
    <div className="mt-2" onClick={(e) => e.preventDefault()}>
      <div className="flex items-center gap-1.5 rounded-sm border bg-white px-2 py-1" style={{ borderColor: !value ? "#D6D3D1" : bad ? "#F59E0B" : "#10B981" }}>
        <Instagram size={13} className={!value ? "text-stone-400" : !bad ? "text-emerald-600" : "text-amber-600"} />
        <input data-testid={`been-there-ig-${id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste your Instagram post link"
          className="w-full bg-transparent text-xs outline-none py-1" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} />
      </div>
      <div className={`text-[11px] mt-1 ${!value ? "text-stone-500" : !bad ? "text-emerald-700" : "text-amber-700"}`}>{!value ? "Optional — add your post to show “See my visit”" : /\/stories\//.test(value) ? "Stories expire — use the link of a feed post or reel" : bad ? "Use a post link like instagram.com/p/…" : "Visit verified by your post"}</div>
    </div>
  );
};

const Option = ({ o, on, toggle, ig, setIg }) => (
  <label data-testid={`been-there-option-${o.id}`} className={`flex gap-3 items-start rounded-sm border p-4 cursor-pointer transition-colors ${on ? "border-[#C9A847] bg-[#FBF6E9]" : "border-stone-200 bg-white hover:border-stone-300"}`}>
    <input type="checkbox" data-testid={`been-there-checkbox-${o.id}`} checked={on} onChange={toggle} className="mt-1 h-4 w-4 accent-[#B08E3A]" />
    <div className="min-w-0">
      <div className="font-display text-lg text-[#2B2A26] leading-snug">{o.name}</div>
      <div className="text-xs text-stone-500">{o.location}</div>
      {o.why && <div className="text-xs text-stone-600 mt-1">{o.why}</div>}
      {on && <div data-testid={`been-there-verified-badge-${o.id}`} className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#2B2A26] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#E9D48F]"><BadgeCheck size={12} /> ✦ Founder Verified · I’ve been here</div>}
      {on && <IgInput id={o.id} value={ig} onChange={setIg} />}
    </div>
  </label>
);

const Group = ({ g, picked, setPicked }) => {
  const [name, setName] = useState("");
  const [loc, setLoc] = useState("");
  const list = picked[g.key] || [];
  const isOn = (o) => list.some((x) => x.name === o.name);
  const toggle = (o) => setPicked({ ...picked, [g.key]: isOn(o) ? list.filter((x) => x.name !== o.name) : [...list, { name: o.name, location: o.location, instagram: "" }] });
  const igOf = (o) => (list.find((x) => x.name === o.name) || {}).instagram || "";
  const setIg = (o) => (v) => setPicked({ ...picked, [g.key]: list.map((x) => (x.name === o.name ? { ...x, instagram: v } : x)) });
  const custom = list.filter((x) => !g.options.some((o) => o.name === x.name));
  const add = () => {
    if (!name.trim()) return;
    setPicked({ ...picked, [g.key]: [...list, { name: name.trim(), location: loc.trim(), instagram: "" }] });
    setName(""); setLoc("");
  };
  return (
    <div data-testid={`been-there-group-${g.key}`} className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display text-2xl text-[#2B2A26]">{g.title}</h3>
        <span data-testid={`been-there-count-${g.key}`} className={`text-xs font-mono-ui ${list.length >= g.need ? "text-emerald-600" : "text-amber-600"}`}>{list.length} ticked · tier calls for {g.need}</span>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {g.options.map((o) => <Option key={o.id} o={o} on={isOn(o)} toggle={() => toggle(o)} ig={igOf(o)} setIg={setIg(o)} />)}
        {custom.map((o, i) => <Option key={`c${i}`} o={{ ...o, id: `${g.key}-custom-${i}`, why: "Added by you" }} on toggle={() => toggle(o)} ig={igOf(o)} setIg={setIg(o)} />)}
      </div>
      <div className="flex gap-2 mt-3 max-w-xl">
        <Input data-testid={`been-there-add-name-${g.key}`} placeholder="Add a place you've been" value={name} onChange={(e) => setName(e.target.value)} className="bg-white" />
        <Input data-testid={`been-there-add-location-${g.key}`} placeholder="Town / area" value={loc} onChange={(e) => setLoc(e.target.value)} className="bg-white w-40" />
        <button data-testid={`been-there-add-${g.key}`} onClick={add} className="inline-flex items-center gap-1 rounded-sm border border-stone-300 px-3 text-sm text-stone-600 hover:bg-stone-100"><Plus size={14} /> Add</button>
      </div>
    </div>
  );
};

export const BeenTherePanel = ({ kit, reload }) => {
  const [picked, setPicked] = useState(kit.selection || {});
  const [busy, setBusy] = useState(false);
  const [saveState, setSaveState] = useState(kit.selection_saved_at ? "saved" : "idle");
  const first = useRef(true);
  const save = async (quiet) => {
    setSaveState("saving");
    try { await http.put(`/kits/${kit.id}/selection/draft`, { selection: picked }); setSaveState("saved"); if (!quiet) toast.success("Your Been There places are saved"); }
    catch (e) { setSaveState("error"); toast.error(errMsg(e)); }
  };
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setSaveState("dirty");
    const t = setTimeout(() => save(true), 1200);
    return () => clearTimeout(t);
  }, [picked]); // eslint-disable-line react-hooks/exhaustive-deps
  const saveLabel = { saving: "Saving…", saved: "Saved", dirty: "Unsaved changes", error: "Save failed — retry", idle: "Save" }[saveState];
  const all = Object.values(picked).flat();
  const total = all.length;
  const unproven = all.filter((x) => !IG_POST.test((x.instagram || "").trim())).length;
  const badLinks = all.filter((x) => (x.instagram || "").trim() && !IG_POST.test(x.instagram.trim())).length;
  const jump = () => {
    const el = Array.from(document.querySelectorAll("[data-testid^=been-there-ig-]")).find((i) => i.value.trim() && !IG_POST.test(i.value.trim()));
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); setTimeout(() => el.focus(), 400); }
  };
  const build = async () => {
    setBusy(true);
    try { await http.put(`/kits/${kit.id}/selection`, { selection: picked }); toast.success("Writing your Founder's kit"); reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const refresh = async () => {
    try { await http.post(`/kits/${kit.id}/candidates`); toast.success("Finding fresh options"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="been-there-panel" className="rise">
      <div className="rounded-sm border border-[#C9A847]/40 bg-[#FBF6E9] p-6 mb-8 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <div className="eyebrow">Founder Verified</div>
          <p className="text-sm text-stone-600 mt-1 max-w-2xl">Tick every place you have personally been. Your ticks save automatically (or press Save) and each one is marked ✦ Founder Verified. Instagram post links are optional — where you add one, the kit shows a “See my visit” link. Only ticked places are written into this Founder's kit, each marked ✦ “I’ve been here myself”. Sections with nothing ticked are left out.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="been-there-save" onClick={() => save(false)} className="inline-flex items-center gap-2 rounded-sm border border-[#C9A847] bg-white px-4 py-2 text-sm font-semibold text-[#9A7B2C] hover:bg-[#FBF6E9]"><Save size={14} /> Save</button>
          <button data-testid="been-there-refresh" onClick={refresh} className="inline-flex items-center gap-2 rounded-sm border border-stone-300 px-4 py-2 text-sm text-stone-600 hover:bg-white"><RefreshCw size={14} /> New options</button>
          <button data-testid="been-there-build" disabled={busy || !total || badLinks > 0} title={badLinks ? `${badLinks} Instagram link(s) need fixing` : ""} onClick={build} className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] text-white px-5 py-2 text-sm font-semibold uppercase tracking-wider hover:bg-[#B8973A] disabled:opacity-50">
            <Sparkles size={15} /> {badLinks ? `${badLinks} link${badLinks === 1 ? "" : "s"} to fix` : `Write kit with ${total} verified places`}
          </button>
        </div>
      </div>
      {(kit.candidates || []).map((g) => <Group key={g.key} g={g} picked={picked} setPicked={setPicked} />)}
      <div className="h-24" />
      {createPortal(<div data-testid="been-there-sticky" className="fixed bottom-0 right-0 left-64 z-30 border-t border-[#C9A847]/40 bg-[#2B2A26]/95 backdrop-blur px-6 py-3 flex flex-wrap items-center gap-4 text-white">
        <div className="text-sm">
          <b data-testid="been-there-count">{total}</b> Founder Verified place{total === 1 ? "" : "s"}
          {total > 0 && (unproven
            ? <span className="text-stone-300"> · {total - unproven} with an Instagram post (optional)</span>
            : <span className="text-emerald-300"> · all linked to your posts</span>)}
          {!total && <span className="text-stone-300"> · tick the places you've been, then paste each Instagram post link</span>}
        </div>
        <span data-testid="been-there-save-state" className={`text-xs ${saveState === "saved" ? "text-emerald-300" : saveState === "error" ? "text-red-300" : "text-stone-300"}`}>{saveLabel}</span>
        {badLinks > 0 && <span className="text-amber-300 text-sm">· {badLinks} link{badLinks === 1 ? "" : "s"} to fix</span>}
        {badLinks > 0 && <button data-testid="been-there-jump" onClick={jump} className="text-xs underline text-amber-200 hover:text-white">Show me</button>}
        <button data-testid="been-there-save-sticky" onClick={() => save(false)} className="ml-auto inline-flex items-center gap-2 rounded-sm border border-[#C9A847] px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-[#E9D48F] hover:bg-white/10"><Save size={15} /> Save</button>
        <button data-testid="been-there-build-sticky" disabled={busy || !total || badLinks > 0} onClick={build}
          className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] text-[#2B2A26] px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-[#d8b955] disabled:opacity-40">
          <Sparkles size={15} /> Write kit with {total} verified place{total === 1 ? "" : "s"}
        </button>
      </div>, document.body)}
    </div>
  );
};
